import { Canvas } from '@react-three/fiber'
import { Suspense, useEffect, useState } from 'react'
import * as THREE from 'three'
import { EnvironmentMorph } from './EnvironmentMorph'
import { ParticleField } from './ParticleField'
import { VirtualChair } from './VirtualChair'
import { TimeStreaks } from './TimeStreaks'
import { PostFX } from './PostFX'
import { AdaptiveQuality } from './AdaptiveQuality'
import { useExperienceStore } from '../store/experienceStore'
import { QUALITY_PRESETS } from '../config/quality'
import { Z_STACK } from '../config/layout'

function Scene() {
  return (
    <>
      <color attach="background" args={['#04060a']} />
      {/* Soft atmospheric depth — film haze */}
      <fog attach="fog" args={['#0a1018', 28, 95]} />
      <ambientLight intensity={0.28} color="#b8c8e0" />
      <hemisphereLight args={['#6a8cbb', '#1a120c', 0.55]} />
      <directionalLight
        position={[6, 10, 4]}
        intensity={1.15}
        color="#fff2e0"
        castShadow={false}
      />
      <pointLight position={[0, 3.5, 2]} intensity={0.55} color="#88c8ff" distance={40} />
      <pointLight position={[-3, 1, 4]} intensity={0.25} color="#ff9a60" distance={25} />
      <spotLight
        position={[0, 8, 2]}
        angle={0.55}
        penumbra={0.8}
        intensity={0.4}
        color="#d0e8ff"
      />

      <EnvironmentMorph />
      <TimeStreaks />
      <ParticleField />
      <VirtualChair />
      <PostFX />
      <AdaptiveQuality />
    </>
  )
}

function useViewportSize() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 720,
  }))
  useEffect(() => {
    const update = () =>
      setSize({
        width: Math.max(window.innerWidth, 320),
        height: Math.max(window.innerHeight, 240),
      })
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])
  return size
}

export function ExperienceCanvas() {
  const quality = useExperienceStore((s) => s.quality)
  const dprCap = QUALITY_PRESETS[quality].pixelRatioCap
  const { width, height } = useViewportSize()

  useEffect(() => {
    const s = useExperienceStore.getState()
    s.setReady(true)
    if (s.loadProgress < 0.4) s.setLoadProgress(0.4)
  }, [])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_STACK.canvas,
        background: '#04060a',
        width,
        height,
      }}
    >
      <Canvas
        dpr={[1, dprCap]}
        gl={{
          antialias: true,
          powerPreference: 'high-performance',
          alpha: false,
          stencil: false,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.08,
          outputColorSpace: THREE.SRGBColorSpace,
        }}
        camera={{ position: [0, 0.55, 3.6], fov: 52, near: 0.1, far: 300 }}
        style={{ width, height, display: 'block' }}
        onCreated={({ gl, camera }) => {
          gl.setClearColor('#04060a', 1)
          // Look slightly down so horizon / city / land sit in frame (Lucy seat POV)
          camera.lookAt(0, -0.35, 0)
        }}
      >
        <Suspense fallback={null}>
          <Scene />
        </Suspense>
      </Canvas>
    </div>
  )
}
